/**
 * useAuth — manages authentication state via Cognito/Google OAuth.
 *
 * - Subscribes to authService state changes
 * - Exposes user, isAuthenticated, login, logout, getAccessToken
 * - Handles OAuth callback code exchange on mount
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authService, AuthUser } from '../services/authService';

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(authService.getUser());
  const [isLoading, setIsLoading] = useState(false);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = authService.subscribe((updatedUser) => {
      setUser(updatedUser);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  // Handle OAuth callback on mount (if URL has ?code= param)
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');

    if (code) {
      setIsLoading(true);
      // Clear URL params to prevent re-processing
      window.history.replaceState({}, document.title, url.pathname);
      authService.handleCallback(code).finally(() => {
        setIsLoading(false);
      });
    }
  }, []);

  const login = useCallback(() => {
    setIsLoading(true);
    authService.login();
  }, []);

  const logout = useCallback(() => {
    authService.logout();
  }, []);

  const getAccessToken = useCallback(() => {
    return authService.getAccessToken();
  }, []);

  const isAuthenticated = useMemo(() => {
    return user !== null && authService.isLoggedIn();
  }, [user]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };
}
